import { useState } from 'react'
import { useQuery } from 'react-query'
import {
  Shield,
  Search,
  Filter,
  Download,
  User,
  Activity,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  Trash2
} from 'lucide-react'
import { auditService, AUDIT_LOG_TYPES, formatAuditLog } from '@/services/audit'
import { useAuth } from '@/context/AuthContext'
import { USER_ROLES } from '@/utils/constants'
import toast from 'react-hot-toast'

const AuditLogs = () => {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useState({
    log_type: AUDIT_LOG_TYPES.ALL,
    skip: 0,
    limit: 20,
    start_date: null,
    end_date: null,
    action: null,
    severity: null,
    success: null,
    search_text: null,
    user_id: null
  })

  const [showFilters, setShowFilters] = useState(false)
  const [selectedLog, setSelectedLog] = useState(null)
  const [exportLoading, setExportLoading] = useState(false)
  const [showCleanupModal, setShowCleanupModal] = useState(false)
  const [cleanupLoading, setCleanupLoading] = useState(false)
  const [retentionDays, setRetentionDays] = useState(365)

  // Fetch audit logs
  const {
    data: auditData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['audit-logs', searchParams],
    () => auditService.searchLogs(searchParams),
    {
      keepPreviousData: true,
      staleTime: 30000 // 30 seconds
    }
  )

  // Fetch audit configuration
  const { data: configData } = useQuery(
    'audit-config',
    () => auditService.getConfiguration(),
    {
      staleTime: 300000 // 5 minutes
    }
  )

  const logs = auditData?.logs || []
  const totalCount = auditData?.total_count || 0
  const hasNext = auditData?.has_next || false
  const hasPrevious = auditData?.has_previous || false

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault()
    setSearchParams(prev => ({ ...prev, skip: 0 }))
    refetch()
  }

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setSearchParams(prev => ({
      ...prev,
      [key]: value,
      skip: 0 // Reset to first page when filtering
    }))
  }

  // Handle pagination
  const handlePageChange = (direction) => {
    const newSkip = direction === 'next'
      ? searchParams.skip + searchParams.limit
      : Math.max(0, searchParams.skip - searchParams.limit)

    setSearchParams(prev => ({ ...prev, skip: newSkip }))
  }

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setSearchParams(prev => ({
      ...prev,
      limit: newPageSize,
      skip: 0 // Reset to first page when changing page size
    }))
  }

  // Handle log detail view
  const handleViewDetails = (log) => {
    setSelectedLog(log)
  }

  const handleExport = async (format = 'csv') => {
    try {
      setExportLoading(true)

      // Handle "all" log type case - export each log type separately and combine
      if (searchParams.log_type === 'all') {
        const logTypes = ['user', 'data_input', 'workflow', 'security', 'system']
        let allExportResponses = []

        for (const logType of logTypes) {
          try {
            // Clean search criteria for this log type
            const cleanedSearchParams = {
              ...searchParams,
              log_type: logType, // Use specific log type instead of "all"
              user_id: searchParams.user_id || null,
              action: searchParams.action || null,
              resource_type: searchParams.resource_type || null,
              severity: searchParams.severity || null,
              success: searchParams.success !== '' ? searchParams.success : null,
              start_date: searchParams.start_date || null,
              end_date: searchParams.end_date || null,
              search_text: searchParams.search_text || null,
              ip_address: searchParams.ip_address || null
            }

            // Prepare export request for this log type
            const exportRequest = {
              search_criteria: cleanedSearchParams,
              export_format: format,
              include_metadata: true,
              filename: `audit_logs_${logType}_${new Date().toISOString().split('T')[0]}`
            }

            // Request export for this log type
            const exportResponse = await auditService.exportLogs(exportRequest)
            if (exportResponse.record_count > 0) {
              allExportResponses.push({ ...exportResponse, logType })
            }
          } catch (error) {
            console.warn(`Failed to export ${logType} logs:`, error)
          }
        }

        // Download all files
        for (const exportResponse of allExportResponses) {
          const blob = await auditService.downloadExport(exportResponse.export_id, format)

          // Create download link
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = exportResponse.filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        }

        const totalRecords = allExportResponses.reduce((sum, resp) => sum + resp.record_count, 0)
        toast.success(`Exported ${totalRecords} audit logs across ${allExportResponses.length} log types`)
      } else {
        // Handle specific log type export
        // Clean search criteria - remove empty strings and convert to null
        const cleanedSearchParams = {
          ...searchParams,
          user_id: searchParams.user_id || null,
          action: searchParams.action || null,
          resource_type: searchParams.resource_type || null,
          severity: searchParams.severity || null,
          success: searchParams.success !== '' ? searchParams.success : null,
          start_date: searchParams.start_date || null,
          end_date: searchParams.end_date || null,
          search_text: searchParams.search_text || null,
          ip_address: searchParams.ip_address || null
        }

        // Prepare export request
        const exportRequest = {
          search_criteria: cleanedSearchParams,
          export_format: format,
          include_metadata: true,
          filename: `audit_logs_${searchParams.log_type}_${new Date().toISOString().split('T')[0]}`
        }

        // Request export
        const exportResponse = await auditService.exportLogs(exportRequest)

        // Download the file
        const blob = await auditService.downloadExport(exportResponse.export_id, format)

        // Create download link
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = exportResponse.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        toast.success(`Exported ${exportResponse.record_count} audit logs successfully`)
      }
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export audit logs')
    } finally {
      setExportLoading(false)
    }
  }

  const handleCleanup = async () => {
    try {
      setCleanupLoading(true)

      // Handle "all" log type case - cleanup each log type separately
      if (searchParams.log_type === 'all') {
        const logTypes = ['user', 'data_input', 'workflow', 'security', 'system']
        let totalCleaned = 0
        let cleanupResults = []

        for (const logType of logTypes) {
          try {
            const response = await auditService.cleanupLogs({
              log_type: logType,
              retention_days: retentionDays
            })
            totalCleaned += response.cleaned_count
            if (response.cleaned_count > 0) {
              cleanupResults.push(`${response.cleaned_count} ${logType} logs`)
            }
          } catch (error) {
            console.warn(`Failed to cleanup ${logType} logs:`, error)
          }
        }

        if (totalCleaned > 0) {
          toast.success(`Cleaned up ${totalCleaned} old audit logs (${cleanupResults.join(', ')})`)
        } else {
          toast.info('No old audit logs found to cleanup')
        }
      } else {
        // Handle specific log type cleanup
        const response = await auditService.cleanupLogs({
          log_type: searchParams.log_type,
          retention_days: retentionDays
        })

        if (response.cleaned_count > 0) {
          toast.success(`Cleaned up ${response.cleaned_count} old ${searchParams.log_type} audit logs`)
        } else {
          toast.info(`No old ${searchParams.log_type} audit logs found to cleanup`)
        }
      }

      setShowCleanupModal(false)

      // Refresh the logs
      refetch()
    } catch (error) {
      console.error('Cleanup failed:', error)
      toast.error('Failed to cleanup audit logs')
    } finally {
      setCleanupLoading(false)
    }
  }

  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return ''
    return new Date(date).toISOString().slice(0, 16)
  }

  // Handle date range preset
  const handleDatePreset = (preset) => {
    const now = new Date()
    let startDate = new Date()

    switch (preset) {
      case 'today':
        startDate.setHours(0, 0, 0, 0)
        break
      case 'week':
        startDate.setDate(now.getDate() - 7)
        break
      case 'month':
        startDate.setMonth(now.getMonth() - 1)
        break
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3)
        break
      default:
        startDate = null
    }

    setSearchParams(prev => ({
      ...prev,
      start_date: startDate?.toISOString(),
      end_date: now.toISOString(),
      skip: 0
    }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
              <p className="text-sm text-gray-600">
                Monitor system activities and security events
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-outline btn-sm flex items-center ${
                showFilters ? 'bg-blue-50 text-blue-700 border-blue-300' : ''
              }`}
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            {user?.role === USER_ROLES.ADMIN && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleExport('csv')}
                  disabled={exportLoading}
                  className="btn-outline btn-sm flex items-center"
                >
                  {exportLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export CSV
                </button>
                <button
                  onClick={() => setShowCleanupModal(true)}
                  className="btn-outline btn-sm flex items-center text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Cleanup Old Logs
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Log Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Log Type
              </label>
              <select
                value={searchParams.log_type}
                onChange={(e) => handleFilterChange('log_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Log Types</option>
                {configData?.log_types?.map(type => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            {/* Action */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action
              </label>
              <select
                value={searchParams.action || ''}
                onChange={(e) => handleFilterChange('action', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Actions</option>
                {configData?.actions?.map(action => (
                  <option key={action} value={action}>
                    {action.charAt(0).toUpperCase() + action.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Severity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Severity
              </label>
              <select
                value={searchParams.severity || ''}
                onChange={(e) => handleFilterChange('severity', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Severities</option>
                {configData?.severity_levels?.map(severity => (
                  <option key={severity} value={severity}>
                    {severity.charAt(0).toUpperCase() + severity.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Success Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={searchParams.success === null ? '' : searchParams.success.toString()}
                onChange={(e) => handleFilterChange('success', e.target.value === '' ? null : e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="true">Success</option>
                <option value="false">Failed</option>
              </select>
            </div>
          </div>

          {/* Date Range */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="datetime-local"
                value={formatDateForInput(searchParams.start_date)}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="datetime-local"
                value={formatDateForInput(searchParams.end_date)}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Presets
              </label>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDatePreset('today')}
                  className="px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-50"
                >
                  Today
                </button>
                <button
                  onClick={() => handleDatePreset('week')}
                  className="px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-50"
                >
                  Week
                </button>
                <button
                  onClick={() => handleDatePreset('month')}
                  className="px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-50"
                >
                  Month
                </button>
              </div>
            </div>
          </div>

          {/* Search Text */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Description
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={searchParams.search_text || ''}
                onChange={(e) => handleFilterChange('search_text', e.target.value || null)}
                placeholder="Search in operation descriptions..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleSearch}
                className="btn-primary btn-sm flex items-center"
              >
                <Search className="h-4 w-4 mr-2" />
                Search
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
            <span className="text-gray-500">Loading audit logs...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <AlertTriangle className="h-6 w-6 text-red-500 mr-2" />
            <span className="text-red-600">Failed to load audit logs</span>
          </div>
        ) : logs.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resource
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Severity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => {
                    const formattedLog = formatAuditLog(log)
                    return (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 text-gray-400 mr-2" />
                            {formattedLog.formattedDate}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <User className="h-4 w-4 text-gray-400 mr-2" />
                            {log.user_id || 'System'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.resource_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${formattedLog.severityColor}`}>
                            {log.severity}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {log.success ? (
                            <span className="inline-flex items-center text-green-600">
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Success
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-red-600">
                              <XCircle className="h-4 w-4 mr-1" />
                              Failed
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {log.operation_description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            onClick={() => handleViewDetails(log)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange('previous')}
                  disabled={!hasPrevious}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange('next')}
                  disabled={!hasNext}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center space-x-4">
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{searchParams.skip + 1}</span>
                    {' '}to{' '}
                    <span className="font-medium">
                      {Math.min(searchParams.skip + searchParams.limit, totalCount)}
                    </span>
                    {' '}of{' '}
                    <span className="font-medium">{totalCount}</span>
                    {' '}results
                  </p>

                  {/* Page Size Selector */}
                  <div className="min-w-[120px]">
                    <select
                      value={searchParams.limit}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>
                  </div>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange('previous')}
                      disabled={!hasPrevious}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handlePageChange('next')}
                      disabled={!hasNext}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <Activity className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No audit logs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search criteria or date range.
            </p>
          </div>
        )}
      </div>

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Audit Log Details</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Information</h4>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Timestamp</dt>
                      <dd className="text-sm text-gray-900">{formatAuditLog(selectedLog).formattedDate}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">User ID</dt>
                      <dd className="text-sm text-gray-900">{selectedLog.user_id || 'System'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Action</dt>
                      <dd className="text-sm text-gray-900">{selectedLog.action}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Resource Type</dt>
                      <dd className="text-sm text-gray-900">{selectedLog.resource_type}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Resource ID</dt>
                      <dd className="text-sm text-gray-900">{selectedLog.resource_id || 'N/A'}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Status & Context</h4>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Severity</dt>
                      <dd className="text-sm text-gray-900">{selectedLog.severity}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">Success</dt>
                      <dd className="text-sm text-gray-900">{selectedLog.success ? 'Yes' : 'No'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">IP Address</dt>
                      <dd className="text-sm text-gray-900">{selectedLog.ip_address || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">User Agent</dt>
                      <dd className="text-sm text-gray-900 truncate">{selectedLog.user_agent || 'N/A'}</dd>
                    </div>
                  </dl>
                </div>
              </div>
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Description</h4>
                <p className="text-sm text-gray-700">{selectedLog.operation_description}</p>
              </div>
              {selectedLog.error_message && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Error Message</h4>
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded">{selectedLog.error_message}</p>
                </div>
              )}
              {(selectedLog.old_values || selectedLog.new_values) && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Changes</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedLog.old_values && (
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 mb-2">Old Values</h5>
                        <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto">
                          {JSON.stringify(selectedLog.old_values, null, 2)}
                        </pre>
                      </div>
                    )}
                    {selectedLog.new_values && (
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 mb-2">New Values</h5>
                        <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto">
                          {JSON.stringify(selectedLog.new_values, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cleanup Modal */}
      {showCleanupModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCleanupModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Cleanup Old Audit Logs
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        This will permanently delete audit logs older than the specified retention period.
                        This action cannot be undone.
                      </p>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Retention Period (days)
                        </label>
                        <input
                          type="number"
                          min="30"
                          max="2555"
                          value={retentionDays}
                          onChange={(e) => setRetentionDays(parseInt(e.target.value))}
                          className="form-input w-full"
                          placeholder="365"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Minimum: 30 days, Maximum: 2555 days (7 years)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={handleCleanup}
                  disabled={cleanupLoading}
                >
                  {cleanupLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Cleaning up...
                    </>
                  ) : (
                    'Cleanup Logs'
                  )}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowCleanupModal(false)}
                  disabled={cleanupLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AuditLogs